import { useCallback, useEffect, useState } from 'react';
import { PackageCheck, Search } from 'lucide-react';
import type { BackorderRow } from '@eombor/contracts';
import { Badge, Button, Dialog, EmptyState, Input, Skeleton, cn } from '@eombor/ui';
import { ApiClientError } from '../../../shared/api/client.js';
import { t } from '../../../shared/i18n/index.js';
import { useStockSearch } from '../../stock/model/stock.queries.js';
import { useFulfilBackorders } from '../model/backorder.queries.js';
import type { FulfilItem } from '../api/backorder.api.js';

const FALLBACK = "Biriktirib bo'lmadi";

const errorText = (error: unknown): string =>
  error instanceof ApiClientError ? error.message : FALLBACK;

/** Miqdorni uch xonagacha yaxlitlaydi — ustun `numeric(14,3)`. */
const qtyText = (qty: number): string => String(Math.round(qty * 1000) / 1000);

/** Har harfda so'rov yubormaslik uchun — skladchi so'zni yozib bo'lsin. */
const SEARCH_DEBOUNCE_MS = 300;

/** Ikki qoralama BIR XILmi — `useEffect` ning cheksiz aylanishini to'xtatadi. */
const same = (a: FulfilItem | null, b: FulfilItem | null): boolean => {
  if (a === null || b === null) return a === b;
  return a.batchId === b.batchId && a.cellId === b.cellId && a.qty === b.qty;
};

/**
 * BITTA BILDIRGINING satri — mahsulot, katak va miqdor.
 *
 * Alohida komponent, chunki har satrning O'Z so'rovlari bor (qidiruv,
 * qoldiq) va hook larni ro'yxat ichida aylanma bo'yicha chaqirib
 * bo'lmaydi. Tanlangani yuqoriga `onChange` bilan chiqadi: oyna
 * hammasini bir tugmada yuboradi.
 */
const FulfilLine = ({
  row,
  onChange,
}: {
  row: BackorderRow;
  onChange: (id: string, draft: FulfilItem | null) => void;
}) => {
  const [productId, setProductId] = useState<string | null>(row.nomenclatureId);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  const products = useStockSearch(
    {
      warehouseId: row.warehouseId,
      ...(debounced === '' ? {} : { q: debounced }),
      onlyAvailable: true,
      limit: 20,
    },
    productId === null,
  );

  const stock = useStockSearch(
    {
      warehouseId: row.warehouseId,
      ...(productId === null ? {} : { nomenclatureId: productId }),
      onlyAvailable: true,
      limit: 1,
    },
    productId !== null,
  );

  const group = stock.data?.items[0];
  const places = group?.places ?? [];
  const [placeKey, setPlaceKey] = useState<string | null>(null);
  /* Birinchi katak oldindan tanlangan: aksariyat holda bitta bo'ladi. */
  const chosen = places.find((p) => `${p.batchId}:${p.cellId}` === placeKey) ?? places[0];

  const maxQty = chosen === undefined ? 0 : Math.min(row.requestedQty, chosen.availableQty);
  const [qty, setQty] = useState('');
  const qtyValue = qty === '' ? maxQty : Number(qty);

  const ready =
    chosen !== undefined && Number.isFinite(qtyValue) && qtyValue > 0 && qtyValue <= maxQty;

  useEffect(() => {
    onChange(
      row.id,
      ready && chosen !== undefined
        ? { backorderId: row.id, batchId: chosen.batchId, cellId: chosen.cellId, qty: qtyValue }
        : null,
    );
  }, [onChange, row.id, ready, chosen, qtyValue]);

  const pickProduct = (id: string | null): void => {
    setProductId(id);
    setPlaceKey(null);
    setQty('');
  };

  const requestedUnit = row.unit ?? group?.unit ?? '';

  return (
    <div className="flex flex-col gap-3">
      {/* So'ralgani doim ko'rinib turadi: skladchi boshqa mahsulot
          tanlayotganda odam NIMA so'raganini solishtirib turishi kerak. */}
      <p className="text-sm text-text">
        <span className="text-text-muted">
          {t('backorder.requestedText', { text: row.nomenclatureName ?? row.freeText ?? '' })}
        </span>
        {' · '}
        <span className="tabular font-medium">
          {qtyText(row.requestedQty)} {requestedUnit}
        </span>
      </p>

      {productId === null ? (
        <div className="flex flex-col gap-2">
          <Input
            label={t('backorder.pickProduct')}
            placeholder={t('backorder.productSearchPlaceholder')}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            prefix={<Search size={14} aria-hidden />}
          />
          {products.isPending ? (
            <Skeleton className="h-24 w-full" />
          ) : (products.data?.items ?? []).length === 0 ? (
            <EmptyState
              icon={<PackageCheck size={20} aria-hidden />}
              title={t('backorder.noProducts')}
              description={t('backorder.noProductsHint')}
            />
          ) : (
            <ul className="flex max-h-64 flex-col gap-1 overflow-y-auto">
              {(products.data?.items ?? []).map((item) => (
                <li key={item.nomenclatureId}>
                  <button
                    type="button"
                    onClick={() => pickProduct(item.nomenclatureId)}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-[var(--radius-sm)] border border-border',
                      'bg-surface p-2.5 text-left hover:border-accent',
                    )}
                  >
                    <span className="min-w-0 flex-1 text-sm text-text">
                      <span className="block truncate font-medium">{item.name}</span>
                      <span className="font-mono text-xs text-text-muted">{item.code}</span>
                    </span>
                    <span className="tabular text-sm font-semibold text-text">
                      {qtyText(item.totalAvailableQty)} {item.unit}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <p className="min-w-0 flex-1 text-sm font-medium text-text">
              {group?.name ?? row.nomenclatureName ?? ''}
            </p>
            <Button variant="ghost" size="sm" onClick={() => pickProduct(null)}>
              {t('backorder.changeProduct')}
            </Button>
          </div>

          {stock.isPending ? (
            <Skeleton className="h-24 w-full" />
          ) : places.length === 0 ? (
            /* Bog'langan mahsulot hali kelmagan yoki shu orada boshqa
               hujjatga band qilingan — skladchi boshqasini tanlay oladi. */
            <EmptyState
              icon={<PackageCheck size={20} aria-hidden />}
              title={t('backorder.noStock')}
              description={t('backorder.noProductsHint')}
            />
          ) : (
            <fieldset className="flex flex-col gap-2">
              <legend className="mb-1 text-sm font-medium text-text">
                {t('backorder.pickPlace')}
              </legend>
              {places.map((place) => {
                const key = `${place.batchId}:${place.cellId}`;
                const active =
                  chosen !== undefined && key === `${chosen.batchId}:${chosen.cellId}`;
                return (
                  <label
                    key={key}
                    className={cn(
                      'flex cursor-pointer items-center gap-3 rounded-[var(--radius-sm)] border p-2.5',
                      active ? 'border-accent bg-accent-soft' : 'border-border bg-surface',
                    )}
                  >
                    <input
                      type="radio"
                      /* Guruh nomi BILDIRGI bo'yicha: bitta oynada bir
                         nechta satr bo'lsa, umumiy nom ularni bitta
                         tanlovga qo'shib yuborardi. */
                      name={`place-${row.id}`}
                      checked={active}
                      onChange={() => {
                        setPlaceKey(key);
                        // Miqdor yangi katakning chegarasiga qaytadi.
                        setQty('');
                      }}
                    />
                    <span className="flex-1 text-sm text-text">
                      <span className="font-mono">{place.cellCode}</span>
                      <span className="text-text-muted"> · {place.batchNo}</span>
                    </span>
                    <span className="tabular text-sm font-semibold text-text">
                      {qtyText(place.availableQty)} {place.unit}
                    </span>
                  </label>
                );
              })}
            </fieldset>
          )}
        </>
      )}

      {chosen === undefined || productId === null ? null : (
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-text">{t('backorder.giveQty')}</span>
          <input
            type="number"
            inputMode="decimal"
            min={0}
            max={maxQty}
            step="any"
            value={qty === '' ? qtyText(maxQty) : qty}
            onChange={(event) => setQty(event.target.value)}
            className={cn(
              'w-40 rounded-[var(--radius-sm)] border border-border-control bg-surface',
              'px-3 py-2 text-base text-text hover:border-border-strong',
            )}
          />
          {/* Chegara SABABI ko'rsatiladi: «so'ralgani 10 edi, nega 4»
              degan savol aynan shu yerda tug'iladi. */}
          {maxQty < row.requestedQty ? (
            <span className="text-xs text-warn">
              {t('backorder.partialHint', {
                available: qtyText(chosen.availableQty),
                requested: qtyText(row.requestedQty),
              })}
            </span>
          ) : null}
        </label>
      )}
    </div>
  );
};

/**
 * BIRIKTIRISH — ombordagi partiyadan buyurtmachiga ajratish.
 *
 * Mahsulot OMBORDAN tanlanadi, bildirgidan emas. Skladchi o'xshash
 * mahsulotni (boshqa ishlab chiqaruvchi, yaqin o'lcham) berishi mumkin,
 * bildirgi esa katalogga umuman bog'lanmagan bo'lishi mumkin. Bog'langan
 * bo'lsa, o'sha mahsulot oldindan tanlangan turadi — aksariyat holda
 * skladchi faqat katak va miqdorni tekshiradi.
 *
 * BIR NECHTA bildirgi bitta oynada: skladchi navbatdan bir nechtasini
 * belgilaydi va hammasi BITTA talabnomaga tushadi. Ilgari har bildirgi
 * o'z hujjatini tug'dirardi va bir odam uchta varaqaga uch marta imzo
 * qo'yardi. Ro'yxat bittadan bo'lsa ekran ham o'zgarmaydi — sarlavhada
 * o'sha bildirgining raqami turadi.
 *
 * Taqsimot QO'LDA: o'nta kelib uch kishi beshtadan so'ragan bo'lsa, kimga
 * nechtasini berishni tizim emas, skladchi hal qiladi.
 */
export const FulfilBackorderDialog = ({
  rows,
  onClose,
  onFulfilled,
}: {
  rows: readonly BackorderRow[];
  onClose: () => void;
  /**
   * Tug'ilgan talabnomaning raqami — ekran uni DARROV ochadi.
   *
   * Yo'lni oyna emas, marshrut biladi (`requisitions.tsx` dagi naqsh:
   * sahifalar routerni ko'rmaydi, ular chaqiruv oladi).
   */
  onFulfilled?: (requisitionId: string) => void;
}) => {
  const [drafts, setDrafts] = useState<Record<string, FulfilItem | null>>({});

  /* Barqaror havola: har chizishda yangisi berilsa, bola komponentning
     `useEffect` i cheksiz aylanardi. */
  const change = useCallback((id: string, draft: FulfilItem | null): void => {
    setDrafts((prev) => (same(prev[id] ?? null, draft) ? prev : { ...prev, [id]: draft }));
  }, []);

  const fulfil = useFulfilBackorders();

  const items = rows.map((row) => drafts[row.id] ?? null);
  const ready = items.every((item) => item !== null);
  const first = rows[0];

  const submit = (): void => {
    if (!ready || fulfil.isPending) return;
    fulfil.mutate(items.filter((item): item is FulfilItem => item !== null), {
      onSuccess: (updated) => {
        onClose();
        /*
         * TUG'ILGAN TALABNOMA DARROV OCHILADI.
         *
         * Skladchining keyingi ishi aynan shu hujjatda: u raqamni
         * ko'rishi, imzoga berishi va bosqichni davom ettirishi kerak.
         * Oyna shunchaki yopilsa, u o'zi yaratgan hujjatni ro'yxatdan
         * qidirib yurardi.
         */
        const requisitionId = updated[0]?.requisitionId ?? null;
        if (requisitionId !== null) onFulfilled?.(requisitionId);
      },
    });
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('backorder.fulfilTitle')}
      description={
        rows.length === 1
          ? `${first?.docNo ?? ''} · ${first?.requesterName ?? ''}`
          : t('backorder.fulfilManyHint', {
              count: rows.length,
              name: first?.requesterName ?? '',
            })
      }
      busy={fulfil.isPending}
      onSubmit={submit}
      error={fulfil.isError ? errorText(fulfil.error) : undefined}
      footer={
        <div className="flex w-full items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={fulfil.isPending}>
            {t('backorder.cancel')}
          </Button>
          <Button onClick={submit} disabled={!ready} loading={fulfil.isPending}>
            {t('backorder.fulfil')}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {rows.map((row) => (
          <div
            key={row.id}
            className={cn(
              rows.length > 1 &&
                'rounded-[var(--radius-md)] border border-border bg-surface-2 p-3',
            )}
          >
            {/* Raqam faqat KO'P bo'lganda: bittasida u sarlavhada turadi
                va uni ikki marta yozish bir xil narsani takrorlash bo'lardi. */}
            {rows.length > 1 ? (
              <p className="mb-2 flex items-center gap-2">
                <Badge tone="neutral" mono>
                  {row.docNo}
                </Badge>
              </p>
            ) : null}
            <FulfilLine row={row} onChange={change} />
          </div>
        ))}
      </div>
    </Dialog>
  );
};
